import React from 'react';
import {Button, Divider, Row, Table, TableColumnsType, Tag, Tooltip, Typography} from 'antd';
import Icon, {DeleteOutlined, EditOutlined, PlusOutlined, UserAddOutlined} from '@ant-design/icons';

import StaffManagementDrawer from './StaffManagementDrawer';
import PageTitle from '../../../components/page/PageTitle';
import confirmAction from '../../../helpers/confirmAction';
import {messageType, showMessages} from '../../../helpers/message';
import {useAppSelector} from '../../../hooks/useStore';
import {useResendVerificationEmailMutation} from '../../../store/api/accountApi';
import {
    useDeleteStaffProfileMutation,
    useGetStaffProfilesQuery,
    useRestoreStaffProfileMutation
} from '../../../store/api/staffManagementApi';
import {selectHasStaffRole} from '../../../store/slices/userSlice';
import {BaseError} from '../../../types/apiTypes';
import {AssignableStaffRoles, StaffProfile, staffRolesMap} from '../../../types/staffManagementTypes';

import './staffManagement.less';
import ResendVerification from "../../../assets/resend-verification-icon.svg";
import ResendVerificationDisabled from "../../../assets/resend-verification-icon-disabled.svg"

const {Title, Text} = Typography;

const StaffManagement = () => {
    const compressRows = window.innerWidth <= 950;
    const isAdmin = useAppSelector(state => selectHasStaffRole(state, ['admin']));

    const [page, setPage] = React.useState(1);
    const [perPage, setPerPage] = React.useState(10);
    const [totalRows, setTotalRows] = React.useState(10);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [action, setAction] = React.useState<'Add' | 'Update'>('Add');
    const [currentProfile, setCurrentProfile] = React.useState<StaffProfile | undefined>(undefined);

    const {data: staffProfiles, isSuccess, isError, isFetching: profilesLoading, error} = useGetStaffProfilesQuery({page, perPage});
    const [deleteStaffProfile, {isSuccess: deleteSuccess, isError: isDeleteError, isLoading: deleteLoading, error: deleteError, reset: deleteReset}] = useDeleteStaffProfileMutation();
    const [restoreStaffProfile, {isSuccess: restoreSuccess, isError: isRestoreError, isLoading: restoreLoading, error: restoreError, reset: restoreReset}] = useRestoreStaffProfileMutation();
    const [resendVerificationEmail, {isSuccess: resendSuccess, isError: isResendError, isLoading: resendLoading, error: resendError, reset: resendReset}] = useResendVerificationEmailMutation();

    React.useEffect(() => {
        if (isSuccess && staffProfiles) {
            setTotalRows(staffProfiles.meta.total);
        } else if (isError && error) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [staffProfiles, error, isError, isSuccess]);

    React.useEffect(() => {
        if (deleteSuccess) {
            showMessages('Staff has been successfully deactivated.', messageType.success);
            deleteReset();
        } else if (isDeleteError && deleteError) {
            showMessages((deleteError as BaseError).data?.message, messageType.error);
            deleteReset();
        }
    }, [deleteSuccess, isDeleteError, deleteError, deleteReset]);

    React.useEffect(() => {
        if (restoreSuccess) {
            showMessages('Staff has been successfully reactivated.', messageType.success);
            restoreReset();
        } else if (isRestoreError && restoreError) {
            showMessages((restoreError as BaseError).data?.message, messageType.error);
            restoreReset();
        }
    }, [restoreSuccess, isRestoreError, restoreError, restoreReset]);

    React.useEffect(() => {
        if (resendSuccess) {
            showMessages('Success! We\'ve resent the verification email.', messageType.success);
            resendReset();
        } else if (isResendError && resendError) {
            showMessages((resendError as BaseError).data?.message, messageType.error);
            resendReset();
        }
    }, [resendSuccess, isResendError, resendError, resendReset]);

    const pageChange = (page: number, perPage: number) => {
        setPage(page);
        setPerPage(perPage);
    }

    const handleClose = () => {
        setDrawerOpen(false);
        setCurrentProfile(undefined);
        setAction('Add');
    }

    const ActiveEmailIcon = React.useCallback(() => (
        <img
            src={ResendVerification}
            alt="email icon with loading circle"
        />
    ), []);

    const DisabledEmailIcon = React.useCallback(() => (
        <img
            src={ResendVerificationDisabled}
            alt="greyed out email icon with loading circle"
        />
    ), []);

    const renderActionsColumn = (_: null, profile: StaffProfile) => {
        const isActive = profile.active;
        const isVerified = profile.verified;
        return (
            <div className="staff-manage-action-btn-container">
                {isActive ?
                    <>
                        {isVerified ?
                            <Icon component={DisabledEmailIcon} />
                        :
                            <Tooltip title="Resend verification email">
                                <Icon
                                    component={ActiveEmailIcon}
                                    onClick={() => resendVerificationEmail({'user_id': profile.user_id, 'is_staff': true})}
                                />
                            </Tooltip>
                        }
                        <Divider type="vertical"/>
                        <EditOutlined
                            key="edit"
                            onClick={() => {
                                setDrawerOpen(true);
                                setAction('Update');
                                setCurrentProfile(profile);
                            }}
                        />
                        <Divider type="vertical"/>
                        <DeleteOutlined
                            key="delete"
                            onClick={() => {
                                confirmAction(
                                    'Are you sure you want to deactivate this Staff?',
                                    'Staff will no longer have access to the system once they are deactivated.',
                                    () => deleteStaffProfile(profile.id)
                                );
                            }}
                        />
                    </>
                    :
                    <>
                        <Icon component={DisabledEmailIcon} />
                        <Divider type="vertical"/>
                        <EditOutlined
                            key="edit"
                            className="anticon-disabled"
                            onClick={() => {
                                showMessages('Cannot edit a staff that has been deactivated.', messageType.warn)
                            }}
                        />
                        <Divider type="vertical"/>
                        <UserAddOutlined
                            key="delete"
                            onClick={() => {
                                confirmAction(
                                    'Are you sure you want to reactivate this Staff?',
                                    'This will restore the original permissions assigned to this staff.',
                                    () => restoreStaffProfile(profile.id)
                                );
                            }}
                        />
                    </>
                }
            </div>
        )
    };

    const renderRoleTags = (roles: AssignableStaffRoles[]) => {
        return (
            <span className="staff-manage-role-tag-container">
                {roles.map((role) => {
                    if (!staffRolesMap[role]) return null;
                    return (
                        <Tag
                            key={role}
                            color={staffRolesMap[role].color}
                            className="staff-manage-drawer-select-role-tag"
                        >
                            {staffRolesMap[role].name}
                        </Tag>
                    );
                })}
            </span>
        )
    }

    let columns: TableColumnsType<StaffProfile> = [
        {
            title: <Typography.Text strong>Name</Typography.Text>,
            dataIndex: undefined,
            key: 'name',
            render: (_, profile: StaffProfile) => {
                return <span className="long-text-wrap">{`${profile.first_name} ${profile.last_name}`}</span>
            },
        },
        {
            title: <Typography.Text strong>Email</Typography.Text>,
            dataIndex: 'email',
            key: 'email',
            render: (email: string) => {
                return <span className="long-text-wrap">{email}</span>
            },
        },
        {
            title: <Typography.Text strong>Role</Typography.Text>,
            dataIndex: 'roles',
            key: 'roles',
            render: (roles: AssignableStaffRoles[]) => renderRoleTags(roles),
        },
        {
            title: <Typography.Text strong>Status</Typography.Text>,
            dataIndex: 'active',
            key: 'active',
            render: (active: boolean) => {
                return <span className="long-text-wrap">{active ? 'Active' : 'Inactive'}</span>
            },
        },
        {
            title: <Typography.Text strong>Action</Typography.Text>,
            key: 'action',
                render: (profile: StaffProfile) => renderActionsColumn(null, profile),
        },
    ];

    const expandedRowRender = (record: StaffProfile) => {
        // Hide all columns in an expandable row for mobile sized screen.
        const expandRows = [
            <li key={0} className="staff-manage-expanded-row-list-item">
                <Typography.Text className="staff-manage-expanded-row-list-title" strong>Email: </Typography.Text>
                {record.email}
            </li>,
            <li key={1} className="staff-manage-expanded-row-list-item">
                <Typography.Text className="staff-manage-expanded-row-list-title" strong>Roles: </Typography.Text>
                {renderRoleTags(record.roles)}
            </li>,
            <li key={2} className="staff-manage-expanded-row-list-item">
                <Typography.Text className="staff-manage-expanded-row-list-title" strong>Status: </Typography.Text>
                {record.active ? 'Active' : 'Inactive'}
            </li>,
            <li key={3} className="staff-manage-expanded-action-buttons-row">
                <Typography.Text className="staff-manage-expanded-row-list-title" strong>Action: </Typography.Text>
                {renderActionsColumn(null, record)}
            </li>
        ];

        return (
            <ul className="staff-manage-expanded-row-list">
                {expandRows}
            </ul>
        )
    }

    const paginationAttr = {
        current: page,
        pageSize: perPage,
        total: totalRows,
        showSizeChanger: true,
        simple: window.innerWidth <= 767,
        onChange: pageChange,
        onShowSizeChange: pageChange,
    }

    const StaffListHeader = (
        <Row className="staff-manage-header-row">
            <Title className="staff-manage-list-title" level={5}>Staff List</Title>
            <Button
                type="primary"
                icon={<PlusOutlined/>}
                onClick={() => setDrawerOpen(true)}
            >
                Add New
            </Button>
        </Row>
    );

    // Only show 'name' column in mobile.
    columns = compressRows ? columns.slice(0, 1) : columns;

    return (
        <>
            <div className="staff-manage-page-title">
                <PageTitle title="Staff Management"/>
            </div>
            {!isAdmin
                ?
                <Text className="role-disclaimer-text">
                    Sorry, you do not have the required permissions to view this page.
                </Text>
                :
                <>
                    <StaffManagementDrawer
                        drawerOpen={drawerOpen}
                        handleClose={handleClose}
                        action={action}
                        profile={currentProfile}
                    />
                    <Table
                        rowKey="id"
                        loading={profilesLoading || deleteLoading || restoreLoading || resendLoading}
                        dataSource={staffProfiles?.data}
                        columns={columns}
                        expandable={{expandedRowRender: compressRows ? expandedRowRender : undefined}}
                        bordered={false}
                        pagination={paginationAttr}
                        caption={StaffListHeader}
                    />
                </>
            }
        </>
    )
}

export default StaffManagement;
